/** @jsxImportSource @emotion/react */
import { useState, useRef, useMemo } from "react";
import {
  downloadFile,
  formatFecha,
  Boton,
  Titulo,
  TablaFiltrable
} from "../../../shared";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { Tooltip } from "primereact/tooltip";
import { Menu } from "primereact/menu";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import {
  accessAPI,
  useIsMobile,
  conIva,
  useIva,
  usePermiso
} from "../../../shared";
import SidebarFactura from "../../facturacion/SidebarFactura";

const SEARCH_FIELDS = ["numeroFactura", "_solicitud"];

export default function CardFacturas({ facturas, loading, onRecargar }) {
  // El menú de acciones de cada factura es solo para quien tiene full
  const { puedeEditar } = usePermiso("admin.facturacion");
  const iva = useIva();
  const [facturaIdDetalle, setFacturaIdDetalle] = useState(null);
  const [facturaSeleccionada, setFacturaSeleccionada] = useState(null);
  const [facturaEnProceso, setFacturaEnProceso] = useState(null);

  const toast = useRef(null);
  const menuRef = useRef(null);
  const isMobile = useIsMobile();

  const facturasConSolicitud = useMemo(() => {
    if (!facturas) return [];
    return facturas.map((f) => {
      const descripciones = (f.procesos || []).map((p) => p.descripcion).filter(Boolean);
      const solicitudes = (f.propuestas || []).map((p) => p.solicitud?.nombrefantasia).filter(Boolean);
      return {
        ...f,
        _solicitud: descripciones.length > 0
          ? descripciones.join(", ")
          : solicitudes.length > 0
            ? solicitudes.join(", ")
            : "-",
      };
    });
  }, [facturas]);

  function marcarPagada(facturaId, archivo) {
    setFacturaEnProceso(facturaId);
    accessAPI(
      "PUT",
      `admin/facturacion/${facturaId}/pagar`,
      null,
      (respuesta) => {
        setFacturaEnProceso(null);
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: respuesta[0]?.msg || "Factura marcada como pagada"
        });
        onRecargar?.();
      },
      (respuesta) => {
        setFacturaEnProceso(null);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al marcar factura como pagada",
          sticky: true
        });
      },
      archivo
    );
  }

  function descargarRecibo(factura) {
    setFacturaEnProceso(factura.id);
    downloadFile(
      `admin/facturacion/${factura.id}/recibo`,
      `Recibo-${factura.numeroFactura || factura.id}.pdf`,
      () => setFacturaEnProceso(null),
      (respuesta) => {
        setFacturaEnProceso(null);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al descargar recibo",
          sticky: true
        });
      }
    );
  }

  function descargarPDF(factura) {
    setFacturaEnProceso(factura.id);
    downloadFile(
      `admin/facturacion/${factura.id}/pdf`,
      `Factura-${factura.numeroFactura || factura.id}.pdf`,
      () => setFacturaEnProceso(null),
      (respuesta) => {
        setFacturaEnProceso(null);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al descargar factura",
          sticky: true
        });
      }
    );
  }

  function generarNotaCredito(facturaId) {
    setFacturaEnProceso(facturaId);
    accessAPI(
      "POST",
      `admin/facturacion/${facturaId}/notacredito`,
      null,
      (respuesta) => {
        setFacturaEnProceso(null);
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: respuesta[0]?.msg || "Nota de crédito emitida"
        });
        onRecargar?.();
      },
      (respuesta) => {
        setFacturaEnProceso(null);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al emitir nota de crédito",
          sticky: true
        });
      }
    );
  }

  function descargarNotaCredito(factura) {
    setFacturaEnProceso(factura.id);
    downloadFile(
      `admin/facturacion/${factura.id}/notacredito`,
      `NotaDeCredito-${factura.numeroFactura || factura.id}.pdf`,
      () => setFacturaEnProceso(null),
      (respuesta) => {
        setFacturaEnProceso(null);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al descargar nota de crédito",
          sticky: true
        });
      }
    );
  }

  const menuItems = facturaSeleccionada
    ? [
        ...(!facturaSeleccionada.fechaPago && !facturaSeleccionada.anulada
          ? [
              {
                label: "Marcar como pagada",
                icon: "pi pi-check",
                command: () => {
                  confirmDialog({
                    message: "¿Marcar esta factura como pagada sin comprobante?",
                    header: "Confirmar",
                    icon: "pi pi-exclamation-triangle",
                    acceptLabel: "Confirmar",
                    rejectLabel: "Cancelar",
                    accept: () => marcarPagada(facturaSeleccionada.id)
                  });
                }
              }
            ]
          : []),
        {
          label: "Descargar factura",
          icon: "pi pi-download",
          command: () => descargarPDF(facturaSeleccionada),
          disabled: !facturaSeleccionada.idFacturaBiller
        },
        ...(facturaSeleccionada.idReciboBiller
          ? [
              {
                label: "Descargar comprobante de pago",
                icon: "pi pi-download",
                command: () => descargarRecibo(facturaSeleccionada)
              }
            ]
          : []),
        ...(!facturaSeleccionada.fechaPago &&
        !facturaSeleccionada.anulada &&
        facturaSeleccionada.idFacturaBiller
          ? [
              {
                label: "Generar nota de crédito",
                icon: "pi pi-times-circle",
                command: () => {
                  confirmDialog({
                    message:
                      "¿Emitir una nota de crédito en Biller para anular esta factura por el total? Esta acción no se puede deshacer.",
                    header: "Confirmar nota de crédito",
                    icon: "pi pi-exclamation-triangle",
                    acceptLabel: "Emitir nota de crédito",
                    rejectLabel: "Cancelar",
                    acceptClassName: "p-button-danger",
                    accept: () => generarNotaCredito(facturaSeleccionada.id)
                  });
                }
              }
            ]
          : []),
        ...(facturaSeleccionada.idNotaDeCreditoBiller ||
        facturaSeleccionada.archivoNotaCreditoId
          ? [
              {
                label: "Descargar nota de crédito",
                icon: "pi pi-download",
                command: () => descargarNotaCredito(facturaSeleccionada)
              }
            ]
          : [])
      ]
    : [];

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <ConfirmDialog />
      <Menu model={menuItems} popup ref={menuRef} />

      <SidebarFactura
        facturaId={facturaIdDetalle}
        onHide={() => setFacturaIdDetalle(null)}
        onAccion={onRecargar}
      />

      <Card>
        {loading ? (
          <p css={{ color: "var(--grisTextos)" }}>Cargando...</p>
        ) : (
          <>
            <Titulo>Facturas</Titulo>
            <TablaFiltrable
              value={facturasConSolicitud}
              searchFields={SEARCH_FIELDS}
              anchoBusqueda="33%"
              nombreArchivo="facturas_cliente"
              dataKey="id"
              stripedRows
              emptyMessage="No hay facturas"
              key={`facturas-${facturaEnProceso}`}
              onRowClick={(e) => {
              const target = e.originalEvent.target;
              if (target.closest("button") || target.closest(".p-menu")) return;
              setFacturaIdDetalle(e.data.id);
            }}
            rowClassName={() => "cursor-pointer"}
          >
            <Column
              header="Número"
              field="numeroFactura"
              style={{ width: "10em" }}
            />
            {!isMobile && (
              <Column
                header="Descripción"
                body={(f) => {
                  const items = (f.procesos || []).map((p) => p.descripcion).filter(Boolean);
                  const lista = items.length > 0
                    ? items
                    : (f.propuestas || []).map((p) => p.solicitud?.nombrefantasia).filter(Boolean);
                  if (lista.length === 0) return "-";
                  const visibles = lista.slice(0, 2);
                  const hayMas = lista.length > 2;
                  return visibles.join(", ") + (hayMas ? "…" : "");
                }}
              />
            )}
            {!isMobile && (
              <Column
                header="Monto"
                body={(f) => {
                  const simbolo = f.moneda?.simbolo || "";
                  const base = `${simbolo} ${f.monto}`;
                  if (!f.masIva) return base;
                  const total = conIva(f.monto, iva).toFixed(2);
                  return (
                    <>
                      <Tooltip target={`.monto-iva-${f.id}`} />
                      <span
                        className={`monto-iva-${f.id}`}
                        data-pr-tooltip={`${simbolo} ${total}`}
                        data-pr-position="top"
                      >
                        {base} + IVA
                      </span>
                    </>
                  );
                }}
                style={{ width: "10em" }}
              />
            )}
            {!isMobile && (
              <Column
                header="Fecha"
                body={(f) => formatFecha(f.fechaCreacion)}
                style={{ width: "10em" }}
              />
            )}
            <Column
              header="Estado"
              body={(f) =>
                f.anulada ? (
                  <Tag value="Anulada" severity="danger" />
                ) : f.fechaPago ? (
                  <Tag value="Pagada" severity="success" />
                ) : (
                  <Tag value="Pendiente" severity="warning" />
                )
              }
              style={{ width: "8em", textAlign: "center" }}
            />
            <Column
              body={(row) => (
                <div css={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5em" }}>
                  {facturaEnProceso === row.id && <i className="pi pi-spin pi-spinner" />}
                  {puedeEditar && (
                  <Boton
                    texto
                    negro
                    callback={(e) => {
                      setFacturaSeleccionada(row);
                      menuRef.current.toggle(e);
                    }}
                  >
                    <i className="pi pi-ellipsis-v" />
                  </Boton>
                  )}
                </div>
              )}
              style={{ width: "80px", textAlign: "center" }}
            />
          </TablaFiltrable>
          </>
        )}
      </Card>
    </>
  );
}
